import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X, Plus } from "lucide-react";
import { uploadImage, resolveUrl, apiErr } from "@/lib/api";

export const ImageUpload = ({ value, onChange, label = "Gambar", testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadImage(f);
      onChange(url);
      toast.success("Gambar berhasil diunggah");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div>
      <label className="text-sm text-slate-300 font-medium">{label}</label>
      <div className="mt-2 flex items-center gap-4">
        <div className="relative w-24 h-24 rounded-xl overflow-hidden border border-white/10 bg-navy-soft grid place-items-center shrink-0">
          {value ? (
            <>
              <img src={resolveUrl(value)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => onChange("")} className="absolute top-1 right-1 grid place-items-center w-6 h-6 rounded-full bg-navy/80 text-white hover:bg-red-500">
                <X className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <span className="text-slate-600 text-xs text-center px-2">Belum ada</span>
          )}
        </div>
        <div className="flex-1">
          <input ref={ref} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={testid} />
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 text-sm font-semibold text-white hover:border-cyan/50 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? "Mengunggah..." : "Unggah Gambar"}
          </button>
          <p className="mt-2 text-xs text-slate-500">JPG, PNG, WEBP. Atau tempel URL di bawah.</p>
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://..." className="mt-2 w-full rounded-lg bg-navy-soft border border-white/10 px-3 py-2 text-xs text-slate-300 focus:outline-none focus:border-cyan/50" />
        </div>
      </div>
    </div>
  );
};

export const ImageListUpload = ({ values = [], onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadImage(f));
      onChange([...(values || []), ...urls]);
      toast.success(`${urls.length} foto ditambahkan`);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const remove = (i) => onChange(values.filter((_, k) => k !== i));

  return (
    <div>
      <label className="text-sm text-slate-300 font-medium">Foto Album ({(values || []).length})</label>
      <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-3">
        {(values || []).map((v, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
            <img src={resolveUrl(v)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 grid place-items-center w-6 h-6 rounded-full bg-navy/80 text-white hover:bg-red-500">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="aspect-square rounded-lg border border-dashed border-white/20 grid place-items-center text-slate-500 hover:border-cyan/50 hover:text-cyan disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" multiple onChange={onFile} className="hidden" data-testid={testid} />
    </div>
  );
};
